import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { syncHeapUserLocale } from '~/common/utils/analytics/heap.utils';
import {
  selectCurrentLocale,
  selectProfileId,
} from '~/store/profile/profileSlice.selectors';

/**
 * Keeps the Heap `locale` user property in step with the locale the app renders.
 *
 * Heap keeps its identity across page loads, so the properties sent at login are not
 * resent later; this pushes the locale again on every reload and language change.
 * Before the first `identify` it does nothing, and the login flow sends the locale itself.
 */
const HeapLocaleSync = (): null => {
  const profileId = useSelector(selectProfileId);
  const currentLocale = useSelector(selectCurrentLocale);

  useEffect(() => {
    if (syncHeapUserLocale(profileId, currentLocale)) return;

    // Until the SDK loads, the `heap-init.js` stub reports no identity even when one
    // is persisted, so retry once Heap is ready. A newer locale supersedes this one.
    let isCurrent = true;
    window.heap?.onReady?.(() => {
      if (isCurrent) syncHeapUserLocale(profileId, currentLocale);
    });
    return () => {
      isCurrent = false;
    };
  }, [profileId, currentLocale]);

  return null;
};

export default HeapLocaleSync;
